import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Package, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const btn = "h-9 px-3 rounded-full border font-mono-d text-[9px] uppercase tracking-[0.2em] flex items-center gap-1.5 disabled:opacity-60 transition-colors";
const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

export const ProductionButton = ({ order, headers, onDone }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/orders/${order.session_id}/production`, {}, { headers });
      onDone(r.data);
      toast.success(r.data.production_email === "sent" ? "Marked in production — customer emailed" : "Marked in production (no email sent)");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update order.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button data-testid={`production-btn-${order.session_id}`} onClick={run} disabled={busy} className={`${btn} border-[#e3e2dc] hover:border-[#0a0a0b]`}>
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Package size={12} />} In production
    </button>
  );
};

export const RefundButton = ({ order, headers, onDone }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("full");
  const [item, setItem] = useState(0);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const sid = order.session_id;
  const left = (order.amount_total ?? order.amount ?? 0) - (order.amount_refunded || 0);
  const items = order.items || [];
  const amount = mode === "full" ? left : mode === "item" ? (items[item]?.unit_amount || 0) * (items[item]?.quantity || 1) : Math.round(parseFloat(custom || "0") * 100);
  const valid = amount >= 50 && amount <= left;

  const run = async () => {
    setBusy(true);
    try {
      const body = mode === "item" ? { item_index: item } : mode === "custom" ? { amount_cents: amount } : {};
      const r = await axios.post(`${API}/admin/orders/${sid}/refund`, body, { headers });
      onDone(r.data);
      setOpen(false);
      toast.success(`Refunded ${money(r.data.refund.amount)} via ${r.data.refund.provider === "paypal" ? "PayPal" : "Stripe"}${r.data.refund.email === "sent" ? " — customer emailed" : ""}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refund failed.");
    } finally {
      setBusy(false);
    }
  };

  const opt = (v, label, sub) => (
    <label key={v} className={`flex items-start gap-3 rounded-2xl border p-3 cursor-pointer transition-colors ${mode === v ? "border-[#0a0a0b] bg-[#faf9f6]" : "border-[#e3e2dc] hover:border-[#0a0a0b]/40"}`}>
      <input data-testid={`refund-mode-${v}-${sid}`} type="radio" name={`refund-${sid}`} checked={mode === v} onChange={() => setMode(v)} className="mt-0.5 accent-[#ff2a85]" />
      <span className="flex-1"><span className="block text-sm font-medium">{label}</span>{sub && <span className="block text-[11px] text-[#5f5e5a] mt-0.5">{sub}</span>}</span>
    </label>
  );

  return (
    <>
      <button data-testid={`refund-btn-${sid}`} onClick={() => setOpen(true)} disabled={left < 50} className={`${btn} border-[#e3e2dc] text-[#5f5e5a] hover:border-[#ff2a85] hover:text-[#ff2a85]`}>
        <RotateCcw size={12} /> Refund
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`refund-dialog-${sid}`} className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display font-extrabold uppercase">Refund order #{sid.slice(-8).toUpperCase()}</DialogTitle>
            <DialogDescription>
              {money(left)} is refundable to the customer's original {order.provider === "paypal" ? "PayPal" : "card"} payment{order.amount_refunded ? ` (${money(order.amount_refunded)} already refunded)` : ""}. The customer gets a refund email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {opt("full", `Everything left — ${money(left)}`, "Marks the order refunded")}
            {items.length > 1 && opt("item", "One item", "Refund a single line from this order")}
            {opt("custom", "Custom amount", "Keep the order, return part of the payment")}
          </div>
          {mode === "item" && items.length > 1 && (
            <select data-testid={`refund-item-${sid}`} value={item} onChange={(e) => setItem(Number(e.target.value))} className="h-10 w-full px-3 rounded-xl bg-white border border-[#e3e2dc] text-sm outline-none focus:border-[#ff2a85]/60">
              {items.map((i, idx) => <option key={idx} value={idx}>{i.product_name} · {i.size}{i.color ? ` · ${i.color}` : ""}{i.quantity > 1 ? ` ×${i.quantity}` : ""} — {money(i.unit_amount * i.quantity)}</option>)}
            </select>
          )}
          {mode === "custom" && (
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#5f5e5a]">$</span>
              <input data-testid={`refund-amount-${sid}`} type="number" min="0.50" step="0.01" max={left / 100} value={custom} onChange={(e) => setCustom(e.target.value)} placeholder={(left / 100).toFixed(2)} className="h-10 w-full pl-7 pr-3 rounded-xl bg-white border border-[#e3e2dc] text-sm outline-none focus:border-[#ff2a85]/60" />
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button data-testid={`refund-cancel-${sid}`} onClick={() => setOpen(false)} className="h-10 px-5 rounded-full border border-[#e3e2dc] hover:border-[#0a0a0b] text-xs">Keep order</button>
            <button data-testid={`refund-confirm-${sid}`} onClick={run} disabled={busy || !valid} className="h-10 px-5 rounded-full bg-[#ff2a85] hover:bg-[#e60067] text-white text-xs font-medium disabled:opacity-50 flex items-center gap-2">
              {busy && <Loader2 size={12} className="animate-spin" />} Refund {valid ? money(amount) : ""}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
